// ResetPasswordPage — password reset for every agent.
//   /reset-password?id=<bot>                 -> ask for a reset message (email)
//   /reset-password?id=<bot>&token=<token>   -> set a new password (link from the email)
// The Robot Factory API endpoints behind this are placeholders for now: see
// requestPasswordReset / completePasswordReset in src/utils/botFactoryAPI.tsx.
// Both forms always submit. Until the account API serves those endpoints (and can send its email), the
// request fails as 'unavailable' and the page shows RESET_PENDING_NOTICE: no build flag, no redeploy to turn on.
import React, { useMemo, useState } from 'react';
import { requestPasswordReset, completePasswordReset, PasswordResetError } from '../utils/botFactoryAPI';
import { usePreAuthOrg } from '../utils/usePreAuthOrg';
import { PreAuthNotice } from '../components/header/PreAuthNotice';

type Phase = 'form' | 'sent' | 'done';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RESET_PENDING_NOTICE =
  'Password reset emails are being set up and will be available soon. Please check back shortly.';

function ResetPasswordPage() {
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const botId = queryParams.get('id') || '';
  const token = queryParams.get('token') || '';
  const hasToken = token !== '';

  // Same branding source as the sign-in and sign-up pages: the agent record from the bootstrap API.
  const pre = usePreAuthOrg(botId || null);
  const org: any = botId && pre.status === 'ready' ? pre.org : null;

  const pageBg = org?.containerStyle?.backgroundColor || '#0a2740';
  const pageFg = org?.bodyStyle?.color || org?.containerStyle?.color || '#e8eef5';
  const buttonBg = org?.buttonStyle?.backgroundColor || '#004c8c';
  const buttonFg = org?.buttonStyle?.color || '#ffffff';
  const logo = org?.logo || '/assets/app/logo192.png';
  const title = org?.name || org?.title || '';

  const [phase, setPhase] = useState<Phase>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState(''); // neutral, not an error: reset is not switched on yet
  const [busy, setBusy] = useState(false);

  // Keep the agent (?id=) so the sign-in page and its branding come back; never carry the token.
  const signInUrl = botId ? `/signin?id=${encodeURIComponent(botId)}` : '/signin';
  const requestAnotherUrl = botId ? `/reset-password?id=${encodeURIComponent(botId)}` : '/reset-password';

  const wordFailure = (error: unknown): string => {
    if (error instanceof PasswordResetError) {
      switch (error.code) {
        case 'expired':
          return 'This reset link has expired. Request a new one.';
        case 'invalid':
          return hasToken ? 'This reset link is not valid. Request a new one.' : 'Check the email address and try again.';
        default:
          return error.message || 'Something went wrong. Please try again.';
      }
    }
    return 'Something went wrong. Please try again.';
  };

  // 'unavailable' = the reset service is not there yet (404 / 5xx / no answer): a neutral notice, not an error.
  const showFailure = (error: unknown) => {
    if (error instanceof PasswordResetError && error.code === 'unavailable') setNotice(RESET_PENDING_NOTICE);
    else setMessage(wordFailure(error));
  };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setNotice('');
    if (!EMAIL_REGEX.test(email.trim())) {
      setMessage('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      await requestPasswordReset(email.trim(), botId || undefined);
      setPhase('sent');
    } catch (error) {
      showFailure(error);
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setNotice('');
    if (password.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      return;
    }
    if (password !== passwordAgain) {
      setMessage('Passwords do not match. Please re-enter.');
      return;
    }
    setBusy(true);
    try {
      await completePasswordReset(token, password);
      setPhase('done');
    } catch (error) {
      showFailure(error);
    } finally {
      setBusy(false);
    }
  };

  // An id that does not resolve is a state, never a generic form for it (same rule as sign up).
  if (botId && pre.status !== 'ready' && pre.status !== 'loading') return <PreAuthNotice status={pre.status} />;

  return (
    <div id="pageContainer" className="resetPage" style={{ backgroundColor: pageBg, color: pageFg }}>
      <style>{`
        html, body, #root { background-color: ${pageBg} !important; }
        .resetPage { min-height: 100dvh; width: 100%; box-sizing: border-box; padding: 32px 16px 64px;
          display: flex; flex-direction: column; align-items: center; }
        .resetPage * { box-sizing: border-box; }
        .resetCard { width: 100%; max-width: 640px; text-align: center; }
        .resetLogo { display: block; margin: 0 auto 12px; max-height: 80px; max-width: min(260px, 80%);
          width: auto; height: auto; object-fit: contain; }
        .resetTitle { font-size: 1.25rem; font-weight: 700; margin: 0 0 6px; }
        .resetLead { font-size: 0.95rem; margin: 0 0 18px; opacity: 0.9; }
        .resetForm { width: 100%; display: flex; flex-direction: column; gap: 10px; text-align: left; }
        .resetLabel { font-size: 12px; line-height: 1.3; opacity: 0.85; }
        .resetInput { width: 100%; padding: 10px 12px; font-size: 16px; border: 1px solid #ccc;
          border-radius: 6px; background: #fff; color: #222; }
        .resetButtons { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; }
        .resetButton { flex: 1 1 140px; padding: 10px 18px; border: none; border-radius: 24px;
          font-weight: 600; font-size: 16px; cursor: pointer; background: ${buttonBg}; color: ${buttonFg}; }
        .resetButton:disabled { opacity: 0.6; cursor: default; }
        .resetButton--plain { background: transparent; color: ${pageFg}; border: 1px solid ${pageFg}; }
        .resetMessage { margin: 0 0 12px; padding: 10px 12px; border-radius: 6px; font-size: 0.95rem;
          background: #fff; color: #7a1c1c; text-align: left; }
        .resetMessage--info { background: rgba(255, 255, 255, 0.12); color: ${pageFg};
          border: 1px solid rgba(255, 255, 255, 0.35); }
        .resetNote { font-size: 0.95rem; margin: 0 0 16px; }
        .resetLink { color: ${pageFg}; }
      `}</style>

      <div className="resetCard">
        <img className="resetLogo" src={logo} alt={title ? `${title} logo` : 'logo'} />
        {title && <div className="resetTitle">{title}</div>}

        {message && (
          <div className="resetMessage" role="alert">
            {message}
          </div>
        )}
        {notice && (
          <div className="resetMessage resetMessage--info" role="status">
            {notice}
          </div>
        )}

        {phase === 'sent' && (
          <>
            <h1 className="resetTitle">Check your email</h1>
            <p className="resetNote">
              If that address is registered, a reset link is on its way. It can take a few minutes to arrive.
            </p>
            <div className="resetButtons">
              <a className="resetButton" href={signInUrl} style={{ textAlign: 'center', textDecoration: 'none' }}>
                Back to sign in
              </a>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            <h1 className="resetTitle">Password updated</h1>
            <p className="resetNote">Sign in with your new password.</p>
            <div className="resetButtons">
              <a className="resetButton" href={signInUrl} style={{ textAlign: 'center', textDecoration: 'none' }}>
                Sign in
              </a>
            </div>
          </>
        )}

        {phase === 'form' && !hasToken && (
          <>
            <h1 className="resetTitle">Reset your password</h1>
            <p className="resetLead">Enter your email and we will send you a link to set a new password.</p>
            <form className="resetForm" onSubmit={handleRequest} noValidate>
              <div>
                <input
                  id="resetEmail"
                  className="resetInput"
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
                <label className="resetLabel" htmlFor="resetEmail">
                  Enter Email
                </label>
              </div>
              <div className="resetButtons">
                <button className="resetButton" type="submit" disabled={busy}>
                  {busy ? 'Sending…' : 'Send reset link'}
                </button>
                <a
                  className="resetButton resetButton--plain"
                  href={signInUrl}
                  style={{ textAlign: 'center', textDecoration: 'none' }}
                >
                  Back to sign in
                </a>
              </div>
            </form>
          </>
        )}

        {phase === 'form' && hasToken && (
          <>
            <h1 className="resetTitle">Set a new password</h1>
            <p className="resetLead">Choose a password of at least 8 characters.</p>
            <form className="resetForm" onSubmit={handleComplete} noValidate>
              <div>
                <input
                  id="resetPassword"
                  className="resetInput"
                  type="password"
                  autoComplete="new-password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
                <label className="resetLabel" htmlFor="resetPassword">
                  Enter New Password
                </label>
              </div>
              <div>
                <input
                  id="resetPasswordAgain"
                  className="resetInput"
                  type="password"
                  autoComplete="new-password"
                  placeholder="New password again"
                  value={passwordAgain}
                  onChange={(e) => setPasswordAgain(e.target.value)}
                />
                <label className="resetLabel" htmlFor="resetPasswordAgain">
                  Enter New Password Again
                </label>
              </div>
              <div className="resetButtons">
                <button className="resetButton" type="submit" disabled={busy}>
                  {busy ? 'Saving…' : 'Save password'}
                </button>
                <a
                  className="resetButton resetButton--plain"
                  href={requestAnotherUrl}
                  style={{ textAlign: 'center', textDecoration: 'none' }}
                >
                  Request a new link
                </a>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
